/**
 * Auth UI Module
 *
 * Handles login modal and auth state UI updates using Supabase.
 */
import { supabase } from "../api";
import { store, actions, selectors } from "../store";
import { getParcels, getOrders } from "../db";
import { getMapInstance } from "../index";
import { trackEvent } from "../analytics";
import type { User } from "@supabase/supabase-js";

type AuthMode = "login" | "register";

export function initAuthUI(): void {
  if (!supabase) {
    console.error("[Auth] Supabase client not configured");
    return;
  }

  const loginBtn = document.getElementById("loginBtn");
  const buyBtn = document.getElementById("buyBtn") as HTMLElement | null;
  const collectionLink = document.querySelector(
    '.nav-link[data-view="collection"]'
  ) as HTMLElement | null;
  const loginModal = document.getElementById(
    "loginModal"
  ) as HTMLDialogElement | null;
  const loginForm = document.getElementById(
    "loginForm"
  ) as HTMLFormElement | null;
  const loginCancel = document.getElementById("loginCancel");
  const loginToggle = document.getElementById("loginToggle");
  const loginModalTitle = document.getElementById("loginModalTitle");
  const loginToggleText = document.getElementById("loginToggleText");
  const loginSubmit = document.getElementById("loginSubmit");
  const loginError = document.getElementById("loginError");
  const loginEmail = document.getElementById(
    "loginEmail"
  ) as HTMLInputElement | null;
  const loginPassword = document.getElementById(
    "loginPassword"
  ) as HTMLInputElement | null;
  const githubLogin = document.getElementById("githubLogin");

  if (!loginBtn || !loginModal || !loginForm) return;

  let mode: AuthMode = "login";

  // Helper to load parcels and orders for a user
  async function loadUserData(userId: string): Promise<void> {
    try {
      const [userParcels, userOrders] = await Promise.all([
        getParcels(userId),
        getOrders(),
      ]);
      actions.setParcels(userParcels);
      actions.setOrders(userOrders);
    } catch (err) {
      console.error("[Auth] Failed to load user data:", err);
    }
  }

  // Helper to handle user session
  function handleSession(user: User | null): void {
    if (user) {
      actions.login({ id: user.id, email: user.email || "" });
      trackEvent("login");
      loadUserData(user.id);
    } else {
      actions.logout();
      actions.setParcels([]);
      actions.setOrders([]);
    }
  }

  // Listen for auth state changes (fires on init, login, logout, token refresh)
  supabase.auth.onAuthStateChange((_event, session) => {
    handleSession(session?.user || null);
  });

  // Check for existing session on init
  supabase.auth.getSession().then(({ data: { session } }) => {
    handleSession(session?.user || null);
  });

  // Subscribe to auth state changes for UI updates
  store.select(selectors.isAuthenticated, (isAuthenticated) => {
    // Update login button
    loginBtn.textContent = isAuthenticated ? "Logout" : "Login";

    // Show/hide auth-gated elements
    const authGatedElements = [buyBtn, collectionLink];
    authGatedElements.forEach((el) => {
      if (el) {
        el.style.display = isAuthenticated ? "" : "none";
      }
    });

    // If user logs out while on a protected view, redirect to explorer
    if (!isAuthenticated) {
      const currentView = store.getState().currentView;
      if (currentView === "collection") {
        actions.navigateTo("explorer");
      }
    }
  });

  // Login button click - show modal or logout
  loginBtn.addEventListener("click", async () => {
    const isAuthenticated = store.getState().isAuthenticated;

    if (isAuthenticated) {
      // Logout — use 'global' scope to revoke all sessions server-side
      const { error: signOutError } = await supabase.auth.signOut({ scope: "global" });
      if (signOutError) {
        console.error("[Auth] signOut failed:", signOutError.message);
        // Force-clear local auth state as fallback
        try {
          const storageKey = Object.keys(localStorage).find((k) => k.includes("supabase") && k.includes("auth"));
          if (storageKey) localStorage.removeItem(storageKey);
        } catch (_) { /* storage unavailable */ }
        handleSession(null);
      }
    } else {
      // Show login modal
      resetForm();
      loginModal.showModal();
    }
  });

  // GitHub OAuth login
  githubLogin?.addEventListener("click", async () => {
    // Save fractal position to both redirectTo URL params and sessionStorage.
    // sessionStorage survives the OAuth redirect chain (same tab, same origin)
    // and acts as a reliable backup when the PKCE flow drops query params.
    const redirectUrl = new URL(window.location.origin);
    const map = getMapInstance();
    if (map?.config) {
      const c = map.config;
      const posData: Record<string, string> = {
        re: c.re.toString(),
        im: c.im.toString(),
        z: c.zoom.toString(),
        i: c.iterations.toString(),
        c: c.colorScheme,
        r: c.reverseColors.toString(),
        sc: c.smoothColoring.toString(),
        pmin: c.paletteMinIter.toString(),
        pmax: c.paletteMaxIter.toString(),
        swi: c.scaleWithIterations.toString(),
        h: c.shiftHueAmount.toString(),
        s: c.saturateAmount.toString(),
        l: c.lightenAmount.toString(),
        cs: c.colorSpace.toString(),
      };
      // Embed in redirectTo URL
      for (const [key, val] of Object.entries(posData)) {
        redirectUrl.searchParams.set(key, val);
      }
      // Also save to sessionStorage as backup
      try {
        sessionStorage.setItem("fractalot_pre_login_pos", JSON.stringify(posData));
      } catch (_) { /* storage unavailable */ }
    }
    console.log("[Auth] OAuth redirectTo:", redirectUrl.toString());

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: {
        redirectTo: redirectUrl.toString(),
      },
    });

    if (error) {
      showError(error.message);
    } else {
      loginModal.close();
    }
  });

  // Cancel button
  loginCancel?.addEventListener("click", () => {
    loginModal.close();
  });

  // Backdrop click to close
  loginModal.addEventListener("click", (e) => {
    if (e.target === loginModal) {
      loginModal.close();
    }
  });

  // Toggle between login and register
  loginToggle?.addEventListener("click", () => {
    mode = mode === "login" ? "register" : "login";
    updateUIForMode();
  });

  // Form submission (email/password)
  loginForm.addEventListener("submit", async (e) => {
    e.preventDefault();

    const email = loginEmail?.value.trim() || "";
    const password = loginPassword?.value || "";

    if (!email || !password) {
      showError("Please fill in all fields");
      return;
    }

    if (password.length < 6) {
      showError("Password must be at least 6 characters");
      return;
    }

    if (loginSubmit) (loginSubmit as HTMLButtonElement).disabled = true;

    try {
      if (mode === "login") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          showError(error.message);
          return;
        }
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) {
          showError(error.message);
          return;
        }

        // Show confirmation message for email verification
        showError("Check your email for a confirmation link!");
        return;
      }

      // Success - close modal (onAuthStateChange will handle the rest)
      loginModal.close();
    } catch (err) {
      showError("An unexpected error occurred");
      console.error("[Auth] Error:", err);
    } finally {
      if (loginSubmit) (loginSubmit as HTMLButtonElement).disabled = false;
    }
  });

  function updateUIForMode(): void {
    if (loginModalTitle) {
      loginModalTitle.textContent = mode === "login" ? "Login" : "Register";
    }
    if (loginSubmit) {
      loginSubmit.textContent = mode === "login" ? "Login" : "Register";
    }
    if (loginToggleText) {
      loginToggleText.textContent =
        mode === "login" ? "Don't have an account?" : "Already have an account?";
    }
    if (loginToggle) {
      loginToggle.textContent = mode === "login" ? "Register" : "Login";
    }
    if (loginPassword) {
      loginPassword.autocomplete =
        mode === "login" ? "current-password" : "new-password";
    }
    clearError();
  }

  function resetForm(): void {
    mode = "login";
    loginForm?.reset();
    updateUIForMode();
  }

  function showError(message: string): void {
    if (loginError) {
      loginError.textContent = message;
    }
  }

  function clearError(): void {
    if (loginError) {
      loginError.textContent = "";
    }
  }
}
